import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { MessageCircle, User, Bot, RefreshCw, ArrowLeft } from "lucide-react";
import { formatDateShort } from "../../lib/format";
import { useCompany } from "../../contexts/CompanyContext";

const formatDate = (iso, tz) => formatDateShort(iso, tz);

// Extract "reply" field out of the stored raw JSON when present so admins see
// the natural-language reply instead of curly braces.
const prettyAssistant = (content) => {
  if (!content) return "";
  const t = content.trim();
  const tryParse = (s) => {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };
  let stripped = t;
  if (stripped.startsWith("```")) {
    stripped = stripped.replace(/^```(json)?/i, "").replace(/```$/, "").trim();
  }
  const j = tryParse(stripped) || (() => {
    const a = t.indexOf("{");
    const b = t.lastIndexOf("}");
    return a >= 0 && b > a ? tryParse(t.slice(a, b + 1)) : null;
  })();
  return (j && j.reply) || content;
};

export default function AdminWaBot() {
  const { currentCompany } = useCompany();
  const tz = currentCompany?.timezone;
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/wa/bot/conversations");
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const openConversation = async (row) => {
    setSelected(row);
    setMessages([]);
    const r = await api.get(`/wa/bot/conversations/${row.phone_digits}`);
    setMessages(r.data || []);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight">WhatsApp Bot</h1>
            <p className="text-neutral-500 text-sm mt-1">
              Conversaciones entrantes procesadas por el asistente automático.
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            data-testid="wabot-reload-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-neutral-200 bg-white text-sm font-semibold hover:bg-neutral-50 disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>

        <div className="mt-6 grid lg:grid-cols-[380px,1fr] gap-4">
          {/* Conversation list */}
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-100 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {rows.length} conversaciones
            </div>
            <ul className="divide-y divide-neutral-100 max-h-[70vh] overflow-auto">
              {rows.length === 0 && !loading && (
                <li className="px-4 py-6 text-center text-sm text-neutral-500">
                  Aún no hay conversaciones del bot.
                </li>
              )}
              {rows.map((r) => (
                <li key={r.phone_digits}>
                  <button
                    type="button"
                    onClick={() => openConversation(r)}
                    data-testid={`wabot-conv-${r.phone_digits}`}
                    className={`w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors ${
                      selected?.phone_digits === r.phone_digits ? "bg-neutral-100" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageCircle size={14} className="text-company shrink-0" />
                        <span className="text-sm font-semibold">+{r.phone_digits}</span>
                      </div>
                      <span className="text-[10px] text-neutral-400">{formatDate(r.last_at, tz)}</span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1 line-clamp-2">
                      <span className="font-medium text-neutral-700">
                        {r.last_role === "user" ? "Cliente: " : "Bot: "}
                      </span>
                      {prettyAssistant(r.last_content)}
                    </p>
                    <p className="text-[10px] text-neutral-400 mt-1">{r.messages} mensajes</p>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Conversation detail */}
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden min-h-[500px]">
            {!selected ? (
              <div className="h-full flex items-center justify-center text-sm text-neutral-500 p-8">
                Selecciona una conversación para ver el historial.
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(null);
                      setMessages([]);
                    }}
                    className="lg:hidden p-1 rounded-md hover:bg-neutral-100"
                    aria-label="Volver"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <MessageCircle size={14} className="text-company" />
                  <span className="text-sm font-semibold">+{selected.phone_digits}</span>
                  <span className="text-xs text-neutral-500 ml-auto">
                    {messages.length} mensajes
                  </span>
                </div>
                <div className="p-4 space-y-3 overflow-auto max-h-[70vh]" data-testid="wabot-messages">
                  {messages.map((m, i) => {
                    const isUser = m.role === "user";
                    return (
                      <div
                        key={i}
                        className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                            isUser
                              ? "bg-company text-[color:var(--company-primary-contrast)] rounded-br-sm"
                              : "bg-neutral-100 text-neutral-800 rounded-bl-sm"
                          }`}
                        >
                          <div className="flex items-center gap-1 mb-1 opacity-70 text-[10px]">
                            {isUser ? <User size={10} /> : <Bot size={10} />}
                            <span>{isUser ? "Cliente" : "Bot"}</span>
                            <span>· {formatDate(m.created_at, tz)}</span>
                          </div>
                          <div>{isUser ? m.content : prettyAssistant(m.content)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
